<script setup>
import { computed, ref, watch } from 'vue';
import { CalendarDate, DateFormatter, getLocalTimeZone, today } from '@internationalized/date';
import { Calendar as CalendarIcon } from 'lucide-vue-next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { RangeCalendar } from '@/components/ui/range-calendar';

const props = defineProps({
    modelValue: { type: [Date, Array], default: null },
    range: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Pick a date' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    class: { type: String, default: '' },
    // Legacy vue-datepicker props for compatibility
    type: { type: String, default: 'date' },
    mode: { type: String, default: 'single' },
    startDate: { type: Date, default: null },
    timezone: { type: String, default: 'local' }
});

const emits = defineEmits(['update:modelValue']);

const df = new DateFormatter('en-US', {
    dateStyle: 'medium'
});

// Convert JS Date to CalendarDate/CalendarDateTime
const toCalendarValue = (date) => {
    if (!date) return null;
    if (date instanceof Date) {
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // JS months are 0-based
        const day = date.getDate();
        return new CalendarDate(year, month, day);
    }
    return date;
};

// Convert CalendarDate/CalendarDateTime to JS Date
const toJSDate = (calendarValue) => {
    if (!calendarValue) return null;
    return calendarValue.toDate(getLocalTimeZone());
};

// Handle single date selection
const singleValue = ref(toCalendarValue(props.modelValue));

// Handle range selection
const rangeValue = ref(() => {
    if (props.range && Array.isArray(props.modelValue)) {
        return {
            start: toCalendarValue(props.modelValue[0]),
            end: toCalendarValue(props.modelValue[1])
        };
    }
    return {
        start: today(getLocalTimeZone()),
        end: today(getLocalTimeZone()).add({ days: 1 })
    };
});

// Watch for prop changes
watch(
    () => props.modelValue,
    (newValue) => {
        if (props.range && Array.isArray(newValue)) {
            rangeValue.value = {
                start: toCalendarValue(newValue[0]),
                end: toCalendarValue(newValue[1])
            };
        } else if (!props.range && newValue) {
            singleValue.value = toCalendarValue(newValue);
        }
    },
    { immediate: true }
);

// Watch for single value changes
watch(singleValue, (newValue) => {
    if (!props.range) {
        emits('update:modelValue', toJSDate(newValue));
    }
});

// Watch for range value changes
watch(
    rangeValue,
    (newValue) => {
        if (props.range && newValue?.start && newValue?.end) {
            emits('update:modelValue', [toJSDate(newValue.start), toJSDate(newValue.end)]);
        }
    },
    { deep: true }
);

// Display text
const displayText = computed(() => {
    if (props.range) {
        const range = rangeValue.value;
        if (range?.start && range?.end) {
            return `${df.format(toJSDate(range.start))} - ${df.format(toJSDate(range.end))}`;
        }
        return props.placeholder;
    } else {
        if (singleValue.value) {
            return df.format(toJSDate(singleValue.value));
        }
        return props.placeholder;
    }
});
</script>

<template>
    <div :class="cn('grid gap-2', props.class)">
        <Popover>
            <PopoverTrigger as-child>
                <Button
                    variant="outline"
                    :class="
                        cn(
                            'w-full justify-start text-left font-normal',
                            !((props.range && rangeValue?.start) || (!props.range && singleValue)) && 'text-muted-foreground'
                        )
                    "
                    :disabled="props.disabled || props.readonly">
                    <CalendarIcon class="mr-2 h-4 w-4" />
                    {{ displayText }}
                </Button>
            </PopoverTrigger>
            <PopoverContent class="w-auto p-0" align="start">
                <!-- Single Date Picker -->
                <Calendar v-if="!props.range" v-model="singleValue" initial-focus :disabled="props.disabled" />
                <!-- Date Range Picker -->
                <RangeCalendar v-else v-model="rangeValue" initial-focus :number-of-months="2" :disabled="props.disabled" />
            </PopoverContent>
        </Popover>
    </div>
</template>
